import React, { useEffect } from 'react';
import { func, object, bool, string } from 'prop-types';
import classnames from 'classnames';
import { getContext } from '@fiverr-private/fiverr_context';
import { translate } from '@fiverr-private/i18n-react';
import { Button } from '@fiverr-private/button';
import { Container, Section, Stack } from '@fiverr-private/layout_components';
import { Heading, Typography, Text } from '@fiverr-private/typography';
import { trackPageMetaData, trackSignUp } from '../../shared/gtmTracking';
import { FormCheckboxGroup, useFormCheckboxGroup } from '../../shared/FormCheckboxGroup';
import { FormCheckbox, useFormCheckbox } from '../../shared/FormCheckbox';
import { useFormInput, FormInput } from '../../shared/FormInput';
import { useFormTextArea, FormTextArea } from '../../shared/FormTextArea';
import { useFormRadioGroup, FormRadioGroup } from '../../shared/FormRadioGroup';
import { logger } from '../../../lib/logger';
import { TERMS_URL, POLICY_URL } from '../../const';
import { ERROR_CODE } from '../services/affiliate';
import { ErrorMessage } from './components/ErrorMessage';

const PROMOTION_STRATEGY_MIN_LENGTH = 100;
const PROMOTION_STRATEGY_MAX_LENGTH = 200;
const AFFILIATE_YEARS_MIN_LENGTH = 1;
const AFFILIATE_YEARS_MAX_LENGTH = 10;
const OTHER_BRANDS_MIN_LENGTH = 3;
const OTHER_BRANDS_MAX_LENGTH = 30;

const trafficSourcesOptions = [
    'paid_search',
    'content_sites',
    'seo',
    'youtube',
    'product_app_integration',
    'blogs',
    'display_advertising',
    'offline',
    'affiliate_networks',
    'medium',
    'facebook',
    'instagram',
    'linkedin',
    'quora',
    'reddit',
    'pinterest',
    'no_traffic_source',
];

const trafficSourcesSelectionLimit = 3;

const audienceInterestsOptions = [
    'graphics_and_design',
    'digital_marketing',
    'writing_and_translation',
    'video_and_animation',
    'music_and_audio',
    'programming_and_tech',
    'data',
    'business',
    'fun_and_lifestyle',
    'all_fiverr_services',
    'fiverr_logo_maker',
    'illustration',
    'interior_design',
    'ai_services',
];

const isBlockingReason = (reason) =>
    [
        ERROR_CODE.EMAIL_TAKEN,
        ERROR_CODE.FORBIDDEN,
        ERROR_CODE.CLIENT_ERROR,
        ERROR_CODE.CX_UNKNOWN_ERROR,
        ERROR_CODE.CX_EMAIL_TAKEN,
        ERROR_CODE.CX_USERNAME_TAKEN,
    ].includes(reason);

const validatePromotionStrategy = async (value) => {
    if (!value || value.trim().length === 0) {
        return [false, translate('affiliates_signup_perseus.errors.required_field')];
    }

    const trimmedValue = value.trim();

    if (trimmedValue.length < PROMOTION_STRATEGY_MIN_LENGTH) {
        return [false, translate('affiliates_signup_perseus.errors.promotion_strategy_min_length')];
    }

    if (trimmedValue.length > PROMOTION_STRATEGY_MAX_LENGTH) {
        return [
            false,
            translate('affiliates_signup_perseus.errors.promotion_strategy_max_length', {
                params: { maxLength: PROMOTION_STRATEGY_MAX_LENGTH },
            }),
        ];
    }

    return [true, ''];
};

const validateAffiliateYears = async (value) => {
    if (!value || value.trim().length === 0) {
        return [false, translate('affiliates_signup_perseus.errors.required_field')];
    }

    const trimmedValue = value.trim();

    if (trimmedValue.length < AFFILIATE_YEARS_MIN_LENGTH) {
        return [false, translate('affiliates_signup_perseus.errors.affiliate_years_min_length')];
    }

    if (trimmedValue.length > AFFILIATE_YEARS_MAX_LENGTH) {
        return [false, translate('affiliates_signup_perseus.errors.affiliate_years_max_length')];
    }

    return [true, ''];
};

const validateOtherBrands = async (value) => {
    if (!value || value.trim().length === 0) {
        return [false, translate('affiliates_signup_perseus.errors.required_field')];
    }

    const trimmedValue = value.trim();

    if (trimmedValue.length < OTHER_BRANDS_MIN_LENGTH) {
        return [false, translate('affiliates_signup_perseus.errors.other_brands_min_length')];
    }

    if (trimmedValue.length > OTHER_BRANDS_MAX_LENGTH) {
        return [false, translate('affiliates_signup_perseus.errors.other_brands_max_length')];
    }

    return [true, ''];
};

const AdditionalInfo = (props) => {
    const { onBack = Function.prototype, onSubmit, submitFailedReason, formData, submitInProgress } = props;

    const contentPurposeOptions = [
        {
            value: 'b2b',
            text: translate('affiliates_signup_perseus.additional_info.main_content_for.b2b'),
        },
        {
            value: 'b2c',
            text: translate('affiliates_signup_perseus.additional_info.main_content_for.b2c'),
        },
    ];

    const onFirstTouch = (name) => {
        const { userId } = getContext();

        trackSignUp({
            action: 'Form Field',
            formFieldName: name,
            formFields: getFormFieldsLength(),
            formType: 'Sign-up',
            userId,
        });
    };

    const getFormFieldsLength = () =>
        Object.keys({ ...getAdditionalInfoFieldsValues(), termsAndConditions, privacyPolicy }).length;

    const trafficSources = useFormCheckboxGroup({
        name: 'trafficSources',
        initialValue: new Set(formData.trafficSources),
        onFirstTouch,
    });
    const audienceInterestsCategories = useFormCheckboxGroup({
        name: 'audienceInterestsCategories',
        initialValue: new Set(formData.audienceInterestsCategories),
        onFirstTouch,
    });
    const contentPurpose = useFormRadioGroup({
        name: 'contentPurpose',
        initialValue: formData.contentPurpose?.[0] || '',
        required: true,
        onFirstTouch,
    });

    const fiverrPromotionPlanQuestion = useFormTextArea({
        name: 'fiverrPromotionPlanQuestion',
        initialValue: formData.fiverrPromotionPlanQuestion || '',
        onFieldInteracted: onFirstTouch,
        validationFunc: validatePromotionStrategy,
    });

    const workDurationQuestion = useFormInput({
        name: 'workDurationQuestion',
        initialValue: formData.workDurationQuestion || '',
        onFieldInteracted: onFirstTouch,
        validationFunc: validateAffiliateYears,
    });

    const otherBrandsQuestion = useFormInput({
        name: 'otherBrandsQuestion',
        initialValue: formData.otherBrandsQuestion || '',
        onFieldInteracted: onFirstTouch,
        validationFunc: validateOtherBrands,
    });

    const termsAndConditions = useFormCheckbox({
        name: 'termsAndConditions',
        initialValue: false,
        onFirstTouch,
    });
    const privacyPolicy = useFormCheckbox({
        name: 'privacyPolicy',
        initialValue: false,
        onFirstTouch,
    });

    useEffect(() => {
        trackPageMetaData();
    }, []);

    useEffect(() => {
        logger.info('Showing additional info');
    }, []);

    const getAdditionalInfoFieldsValues = () => ({
        trafficSources: [...trafficSources.list],
        audienceInterestsCategories: [...audienceInterestsCategories.list],
        contentPurpose: contentPurpose.value,
        fiverrPromotionPlanQuestion: fiverrPromotionPlanQuestion.value,
        workDurationQuestion: workDurationQuestion.value,
        otherBrandsQuestion: otherBrandsQuestion.value,
    });

    const handleSubmit = () => {
        onSubmit(getAdditionalInfoFieldsValues());
    };

    const handleEnter = (e) => {
        if (e.key !== 'Enter') {
            return;
        }

        if (isFormValid()) {
            handleSubmit();
        }
    };

    const isFormValid = () =>
        trafficSources.list.size > 0 &&
        audienceInterestsCategories.list.size > 0 &&
        !!contentPurpose.value &&
        fiverrPromotionPlanQuestion.valid &&
        !fiverrPromotionPlanQuestion.isEmpty() &&
        workDurationQuestion.valid &&
        !workDurationQuestion.isEmpty() &&
        otherBrandsQuestion.valid &&
        !otherBrandsQuestion.isEmpty() &&
        termsAndConditions.value &&
        privacyPolicy.value &&
        !isBlockingReason(submitFailedReason);

    const templates = {
        policy: (text) => (
            <a rel="noopener noreferrer" target="_blank" href={POLICY_URL}>
                {text}
            </a>
        ),
        terms: (text) => (
            <a rel="noopener noreferrer" target="_blank" href={TERMS_URL}>
                {text}
            </a>
        ),
    };

    return (
        <Container onKeyPress={handleEnter}>
            <Stack rowGap={4}>
                <Heading as="h2" paddingBottom="2">
                    {translate('affiliates_signup_perseus.additional_info.tell_about_yourself')}
                </Heading>
            </Stack>

            <Section marginBottom="3">
                <Stack marginBottom="2">
                    <Heading as="h3" size="b_md">
                        {translate('affiliates_signup_perseus.additional_info.plant_to_promote')}
                    </Heading>
                    <Typography color="red_700"> *</Typography>
                </Stack>
                <Text>{translate('affiliates_signup_perseus.additional_info.assist_information')}</Text>
                <Text marginBottom="3">{translate('affiliates_signup_perseus.additional_info.pick_many')}</Text>

                <FormCheckboxGroup
                    name={trafficSources.name}
                    options={trafficSourcesOptions}
                    withOther
                    translationPrefix="affiliates_signup_perseus.additional_info.how_to_promote"
                    maxSelected={trafficSourcesSelectionLimit}
                    value={trafficSources.value}
                    onChange={trafficSources.onChange}
                    onOtherChange={trafficSources.onOtherChange}
                />
            </Section>

            <Section marginBottom="3">
                <Stack marginBottom="2">
                    <Heading as="h3" size="b_md">
                        {translate('affiliates_signup_perseus.additional_info.audience_information')}
                    </Heading>
                    <Typography color="red_700"> *</Typography>
                </Stack>
                <Text>{translate('affiliates_signup_perseus.additional_info.assist_information')}</Text>
                <Text marginBottom="3">{translate('affiliates_signup_perseus.additional_info.pick_many')}</Text>

                <FormCheckboxGroup
                    name={audienceInterestsCategories.name}
                    options={audienceInterestsOptions}
                    withOther
                    translationPrefix="affiliates_signup_perseus.additional_info.audience_interests"
                    maxSelected={5}
                    value={audienceInterestsCategories.value}
                    onChange={audienceInterestsCategories.onChange}
                    onOtherChange={audienceInterestsCategories.onOtherChange}
                />
            </Section>

            <Section marginBottom="3">
                <Stack marginBottom="2">
                    <Heading as="h3" size="b_md">
                        {translate('affiliates_signup_perseus.additional_info.main_content_information')}
                    </Heading>
                    <Typography color="red_700"> *</Typography>
                </Stack>

                <FormRadioGroup
                    options={contentPurposeOptions}
                    name={contentPurpose.name}
                    value={contentPurpose.value}
                    onChange={contentPurpose.onChange}
                    direction="column"
                />
            </Section>

            <Section marginBottom="3">
                <Stack marginBottom="2">
                    <Heading as="h3" size="b_md">
                        {translate('affiliates_signup_perseus.additional_info.promotion_strategy_label')}
                    </Heading>
                    <Typography color="red_700"> *</Typography>
                </Stack>

                <FormTextArea
                    ref={fiverrPromotionPlanQuestion.ref}
                    placeholder={translate('affiliates_signup_perseus.additional_info.promotion_strategy_placeholder')}
                    value={fiverrPromotionPlanQuestion.value}
                    onFocus={fiverrPromotionPlanQuestion.onFocus}
                    onChange={fiverrPromotionPlanQuestion.onChange}
                    onBlur={fiverrPromotionPlanQuestion.onBlur}
                    isRequired={true}
                    valid={fiverrPromotionPlanQuestion.valid}
                    dirty={fiverrPromotionPlanQuestion.dirty}
                    pending={fiverrPromotionPlanQuestion.pending}
                    reason={fiverrPromotionPlanQuestion.reason}
                    rows={5}
                />
            </Section>

            <Section marginBottom="3">
                <Stack marginBottom="2">
                    <Heading as="h3" size="b_md">
                        {translate('affiliates_signup_perseus.additional_info.affiliate_experience_label')}
                    </Heading>
                    <Typography color="red_700"> *</Typography>
                </Stack>

                <Stack direction="column" gap="3">
                    <FormInput
                        ref={workDurationQuestion.ref}
                        placeholder={translate('affiliates_signup_perseus.additional_info.affiliate_years_placeholder')}
                        value={workDurationQuestion.value}
                        onFocus={workDurationQuestion.onFocus}
                        onChange={workDurationQuestion.onChange}
                        onBlur={workDurationQuestion.onBlur}
                        isRequired={true}
                        valid={workDurationQuestion.valid}
                        dirty={workDurationQuestion.dirty}
                        pending={workDurationQuestion.pending}
                        errorMessage={workDurationQuestion.reason}
                        tip={translate('affiliates_signup_perseus.additional_info.affiliate_years_label')}
                    />

                    <FormInput
                        ref={otherBrandsQuestion.ref}
                        placeholder={translate('affiliates_signup_perseus.additional_info.other_brands_placeholder')}
                        value={otherBrandsQuestion.value}
                        onFocus={otherBrandsQuestion.onFocus}
                        onChange={otherBrandsQuestion.onChange}
                        onBlur={otherBrandsQuestion.onBlur}
                        isRequired={true}
                        valid={otherBrandsQuestion.valid}
                        dirty={otherBrandsQuestion.dirty}
                        pending={otherBrandsQuestion.pending}
                        errorMessage={otherBrandsQuestion.reason}
                        tip={translate('affiliates_signup_perseus.additional_info.other_brands_label')}
                    />
                </Stack>
            </Section>

            <Container paddingBottom="5">
                <Container paddingY="1">
                    <FormCheckbox
                        value={termsAndConditions.value}
                        onChange={termsAndConditions.onChange}
                        onBlur={termsAndConditions.onBlur}
                    >
                        <Text>
                            {translate('affiliates_signup_perseus.additional_info.agree_to_terms', { templates })}
                        </Text>
                    </FormCheckbox>
                </Container>
                <Container paddingY="1">
                    <FormCheckbox
                        value={privacyPolicy.value}
                        onChange={privacyPolicy.onChange}
                        onBlur={privacyPolicy.onBlur}
                    >
                        <Text>
                            {translate('affiliates_signup_perseus.additional_info.agree_to_privacy', { templates })}
                        </Text>
                    </FormCheckbox>
                </Container>
            </Container>

            <Button size="lg" colorScheme="green" disabled={!isFormValid() || submitInProgress} onClick={handleSubmit}>
                {translate('affiliates_signup_perseus.additional_info.register')}
            </Button>
            {submitFailedReason && <ErrorMessage onBack={onBack} reason={submitFailedReason} />}
            {submitInProgress && (
                <Container display="inlineBlock" paddingLeft="2">
                    <Typography size="b_lg">
                        <i className={classnames('fa fa-spinner fa-spin')} />
                    </Typography>
                </Container>
            )}
        </Container>
    );
};

AdditionalInfo.propTypes = {
    onBack: func,
    onSubmit: func,
    submitFailedReason: string,
    formData: object,
    submitInProgress: bool,
};

export default AdditionalInfo;
